import { useCallback, useRef, useState } from 'react'
import useSWR from 'swr'
import {
   ConversationService,
   ConversationItem,
} from '@forwardflow/services/ConversationService'
import { PaginationQueryParams } from '@forwardflow/types'

const useConversation = (
   queryParams: PaginationQueryParams,
   selectedLeadId: number | undefined,
   onInitialLoad?: () => void
) => {
   const [page, setPage] = useState<number>(1)
   const [hasMore, setHasMore] = useState<boolean>(true)
   const [allConversation, setAllConversation] = useState<ConversationItem[]>(
      []
   )
   const initialLoadCompleted = useRef(false)

   const fetcher = async () => {
      if (!selectedLeadId) return []

      const response = await ConversationService.getConversation(
         { ...queryParams, page: 1 },
         selectedLeadId
      )

      if (!initialLoadCompleted.current && onInitialLoad) {
         onInitialLoad()
         initialLoadCompleted.current = true
      }

      setHasMore(response.length === queryParams.limit)
      setAllConversation(response)
      return response
   }

   const { data, error, mutate, isValidating } = useSWR(
      selectedLeadId ? [`/conversation/${selectedLeadId}`, queryParams] : null,
      fetcher,
      {
         revalidateOnFocus: false,
         revalidateOnReconnect: false,
         dedupingInterval: 5000,
         onSuccess: (data) => {
            setAllConversation(data || [])
         },
      }
   )

   const loadMore = useCallback(async () => {
      if (!selectedLeadId || !hasMore || isValidating) return Promise.resolve()

      try {
         const nextPage = page + 1
         const moreMessages = await ConversationService.getConversation(
            { ...queryParams, page: nextPage },
            selectedLeadId
         )

         if (queryParams.limit && moreMessages.length < queryParams.limit) {
            setHasMore(false)
         }

         setPage(nextPage)
         setAllConversation((prev) => [...moreMessages, ...prev])
      } catch (error) {
         console.error('Failed to load more messages', error)
      }

      return Promise.resolve()
   }, [selectedLeadId, hasMore, isValidating, page, queryParams])

   const sendMessage = useCallback(
      async (message: string) => {
         if (!selectedLeadId) return

         try {
            await ConversationService.sendMessage(selectedLeadId, message)
            mutate() // Revalidate to get the new message
         } catch (err) {
            console.error('Failed to send message', err)
         }
      },
      [selectedLeadId, mutate]
   )

   // Reset state when selectedLeadId changes
   if (data && selectedLeadId && data !== allConversation) {
      setAllConversation(data)
      setPage(1)
      initialLoadCompleted.current = false
   }

   return {
      conversation: allConversation,
      loading: isValidating,
      error: error?.message || null,
      sendMessage,
      hasMore,
      loadMore,
      mutate,
   }
}

export default useConversation
